import { useState } from "react";
import { useLocation } from "wouter";

/**
 * Phase 14 — manual claim entry surface.
 *
 * The customer landed on `/claim` after either reading a printed
 * slip without scanning, or after our preview surface bounced them
 * here for code re-entry. Auto-uppercases input + auto-inserts the
 * dash between the platform prefix and the body so what the customer
 * types matches the printed format `XX-XXXXXXXX`.
 *
 * On submit we navigate to `/claim/:code` which renders the preview
 * + claim flow.
 */

function format(raw: string): string {
  // Uppercase, strip everything that isn't an allowed alphabet char or
  // dash. Auto-insert the dash after the 2-letter platform prefix.
  // Allowed alphabet matches the codegen in `marketplace/codegen.ts`
  // (no 0/O/1/I/L) — we permit a forgiving superset on input and let
  // the server's lookup reject typos.
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (cleaned.length <= 2) return cleaned;
  return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}`;
}

export function ClaimEntryPage() {
  const [, navigate] = useLocation();
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const formatted = format(code);
  const stripped = formatted.replace(/-/g, "");
  const canSubmit = stripped.length === 10;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!canSubmit) {
      setErr("Code is 2 letters + 8 characters, e.g. SH-A1B2C3D4.");
      return;
    }
    navigate(`/claim/${stripped}`);
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <h1 className="text-2xl font-bold text-navy">Enter your claim code</h1>
      <p className="mt-2 text-sm text-ink-dark">
        It's printed on the slip inside your package — 2 letters then 8
        characters, like <span className="font-mono">SH-A1B2C3D4</span>.
      </p>

      <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
        <label htmlFor="code" className="label">
          Claim code
        </label>
        <input
          id="code"
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          value={formatted}
          onChange={(e) => setCode(e.target.value)}
          placeholder="SH-A1B2C3D4"
          className="rounded border border-stone-300 px-3 py-2 font-mono text-lg tracking-wider"
          maxLength={11}
        />
        {err && <p className="text-sm text-red-600">{err}</p>}
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded bg-navy px-5 py-2 font-semibold text-white disabled:bg-stone-300"
        >
          Look up code
        </button>
      </form>
    </main>
  );
}
